import { useState, useEffect, useMemo } from "react";
import { Box, CircularProgress, Modal, Typography } from "@mui/material";
import { X, Search, Check } from "lucide-mui";
import { listPdfFiles } from "../api/filesApi";
import { PdfIcon } from "./icons";
import { styledComponent } from "../utils/styled";
import { useTheme } from "../context/ThemeContext";
import {
  PickerModalShell,
  PickerModalHeader,
  PickerModalTitle,
  PickerModalCloseBtn,
  PickerSearchRow,
  PickerSearchBox,
  PickerSearchInput,
  PickerModalBody,
  PickerModalFooter,
  PickerSelectedCount,
  PickerFooterBtns,
  PickerCancelBtn,
  PickerActionBtn,
  PickerBtnLabel,
} from "./ui";

// ── Styled components ─────────────────────────────────────────────────────────

const FileRow = styledComponent(Box)(
  ({ isSelected, isAlreadyAdded, isDark }) => ({
    display: "flex",
    alignItems: "center",
    paddingLeft: "5px",
    paddingRight: "16px",
    paddingTop: "8px",
    paddingBottom: "8px",
    cursor: isAlreadyAdded ? "default" : "pointer",
    ...(isDark
      ? {
          borderRadius: "12px",
          backgroundColor: isSelected ? "#282b2e" : "transparent",
          border: isSelected ? "1px solid #414447" : "1px solid transparent",
        }
      : {
          backgroundColor: isSelected ? "rgba(0,0,0,0.05)" : "transparent",
        }),
    opacity: isAlreadyAdded ? 0.4 : 1,
    "&:hover": !isAlreadyAdded
      ? {
          backgroundColor: isDark
            ? "rgba(255,255,255,0.04)"
            : "rgba(0,0,0,0.04)",
        }
      : {},
  }),
);

const FileIconWrapper = styledComponent(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flex: 1,
  padding: "0 6px",
  minWidth: 0,
});

const FileCheckCircle = styledComponent(Box)({
  width: 18,
  height: 18,
  borderRadius: "50%",
  backgroundColor: "#146E37",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const FileCheckEmpty = styledComponent(Box)(({ isDark }) => ({
  width: 18,
  height: 18,
  borderRadius: "50%",
  border: `1.5px solid ${isDark ? "rgba(255,255,255,0.25)" : "rgba(49,54,59,0.25)"}`,
}));

// ── Component ─────────────────────────────────────────────────────────────────

export function AddFilesModal({
  open,
  onClose,
  onAdd,
  userId,
  sessionId,
  existingFileIds = [],
  isSaving = false,
}) {
  const { isDark } = useTheme();
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set());
    setSearchText("");
    fetchFiles();
  }, [open]);

  const fetchFiles = async () => {
    setIsLoading(true);
    try {
      setFiles(await listPdfFiles(userId, sessionId));
    } catch (e) {
      console.error("Failed to fetch files for source picker:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredFiles = useMemo(() => {
    if (!searchText.trim()) return files;
    const s = searchText.toLowerCase();
    return files.filter(f => f.file_name?.toLowerCase().includes(s));
  }, [files, searchText]);

  const toggleSelect = fileId => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const handleAdd = () => {
    if (selectedIds.size === 0 || isSaving) return;
    onAdd([...selectedIds]);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <PickerModalShell
        isDark={isDark}
        sx={{ width: "472px", maxHeight: "500px" }}
      >
        <PickerModalHeader isDark={isDark}>
          <PickerModalTitle isDark={isDark}>Add Files</PickerModalTitle>
          <PickerModalCloseBtn
            isDark={isDark}
            onClick={onClose}
            sx={{ cursor: "pointer" }}
          >
            <X
              sx={{
                width: 14,
                height: 14,
                color: isDark ? "rgba(255,255,255,0.4)" : "rgba(49,54,59,0.4)",
              }}
            />
          </PickerModalCloseBtn>
        </PickerModalHeader>

        <PickerSearchRow isDark={isDark}>
          <PickerSearchBox isDark={isDark}>
            <Search
              sx={{
                width: 13,
                height: 13,
                color: isDark ? "rgba(255,255,255,0.4)" : "rgba(49,54,59,0.4)",
                flexShrink: 0,
              }}
            />
            <PickerSearchInput
              isDark={isDark}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder='Search files…'
            />
          </PickerSearchBox>
        </PickerSearchRow>

        <PickerModalBody isDark={isDark}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} sx={{ color: "#8C8C8C" }} />
            </Box>
          ) : filteredFiles.length === 0 ? (
            <Typography
              sx={{
                textAlign: "center",
                py: 3,
                color: isDark ? "rgba(255,255,255,0.4)" : "rgba(49,54,59,0.4)",
                fontFamily: "Inter",
                fontSize: "13px",
                fontStyle: "italic",
              }}
            >
              No PDF files found
            </Typography>
          ) : (
            filteredFiles.map(file => {
              const isSelected = selectedIds.has(file.file_id);
              const isAlreadyAdded = existingFileIds.includes(file.file_id);
              return (
                <FileRow
                  key={file.file_id}
                  isDark={isDark}
                  isSelected={isSelected}
                  isAlreadyAdded={isAlreadyAdded}
                  onClick={() => !isAlreadyAdded && toggleSelect(file.file_id)}
                >
                  <FileIconWrapper>
                    <PdfIcon
                      style={{ width: "17px", height: "19px", flexShrink: 0 }}
                    />
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: isDark ? "#eceff3" : "#31363b",
                      }}
                    >
                      {file.file_name}
                    </Typography>
                  </FileIconWrapper>
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isSelected ? (
                      <FileCheckCircle>
                        <Check sx={{ width: 11, height: 11, color: "#fff" }} />
                      </FileCheckCircle>
                    ) : (
                      <FileCheckEmpty isDark={isDark} />
                    )}
                  </Box>
                </FileRow>
              );
            })
          )}
        </PickerModalBody>

        <PickerModalFooter isDark={isDark}>
          <PickerSelectedCount isDark={isDark}>
            <span>{selectedIds.size}</span>
            <span
              style={{
                color: isDark ? "rgba(255,255,255,0.3)" : "rgba(49,54,59,0.3)",
                marginLeft: "3px",
              }}
            >
              selected
            </span>
          </PickerSelectedCount>
          <PickerFooterBtns>
            <PickerCancelBtn
              isDark={isDark}
              onClick={onClose}
              sx={{ cursor: "pointer" }}
            >
              <PickerBtnLabel isDark={isDark}>Cancel</PickerBtnLabel>
            </PickerCancelBtn>
            <PickerActionBtn
              onClick={handleAdd}
              sx={{
                cursor:
                  selectedIds.size > 0 && !isSaving ? "pointer" : "default",
                opacity: selectedIds.size > 0 && !isSaving ? 1 : 0.5,
              }}
            >
              {isSaving ? (
                <CircularProgress size={12} sx={{ color: "#eceff3" }} />
              ) : (
                <PickerBtnLabel isDark={isDark}>Add</PickerBtnLabel>
              )}
            </PickerActionBtn>
          </PickerFooterBtns>
        </PickerModalFooter>
      </PickerModalShell>
    </Modal>
  );
}
